// The CLI driver for the driver-agnostic converge engine (platform-converge.ts).
// buildConvergeDeps wires each ConvergeDeps executor to its concrete
// implementation, resolving persona/agent names via LIVE discovery
// (listM8tAgents) rather than a hardcoded map — an install only has the
// agents it actually deployed, and the manifest may describe more.

import * as path from "node:path";
import type { TokenCredential } from "@azure/identity";
import type { ReleaseManifest } from "./release-manifest.js";
import type { RepoTree } from "./release-content.js";
import type { FoundryProject } from "./foundry-project.js";
import type { ComponentAction, ApplyCtx, ConvergeDeps } from "./platform-converge.js";
import { applyPersona, applyAgentImage, applyGatewayImage, applyInfraDeploy, infraTargetSha } from "./platform-converge.js";
import type { FoundryTracingMode } from "./deploy.js";
import { readStamp, writeStamp, type InstalledStamp } from "./platform-stamp.js";
import { openInfraParamsTable, readInfraParams, writeInfraParams as writeInfraParamsRow } from "./platform-infra-params.js";
import { readLiveVoiceInternalSecret } from "./gateway-live-params.js";
import { listM8tAgents } from "./foundry-discovery.js";
import { awaitAgentQueryable } from "./agent-ready.js";
import { compareSemver, gatewayRefFromComponent } from "./platform-update.js";
import { CLI_VERSION } from "./package-version.js";
import { LocalCliError } from "./errors.js";
import { runSeedRefresh } from "./seed-refresh-driver.js";
import { discoverKvUri } from "./brain-paths.js";
import { readGatewayVersion } from "./gateway-api.js";

// Hosted-agent components' persona-dir name (== metadata.persona set at deploy time
// by `m8t coder deploy` / `m8t azure-exec deploy`), keyed by the manifest ComponentKey.
const HOSTED_AGENT_PERSONA_DIR: Partial<Record<ComponentAction["component"], string>> = {
  codingAgent: "coding-agent",
  azureExecutor: "azure-executor",
};

export interface BuildConvergeDepsArgs {
  credential: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
  contentDir: string;
  manifest: ReleaseManifest;
  tree: RepoTree;
  project: FoundryProject;
  gatewayResourceId: string;
  gatewayUrl?: string;
  suffix?: string;
  onProgress?: (m: string) => void;
  onWarn?: (m: string) => void;
}

/**
 * Build a `ConvergeDeps` for the CLI, backed by the real Azure/Foundry
 * executors in platform-converge.ts. Persona/agent name resolution is
 * DISCOVERED once (a single `listM8tAgents` call) rather than hardcoded —
 * the manifest's persona/component keys are matched against
 * `metadata.persona` on the live-deployed agents. A manifest entry with no
 * matching deployed agent is skipped (warned), never fabricated.
 */
export async function buildConvergeDeps(args: BuildConvergeDepsArgs): Promise<ConvergeDeps> {
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- default no-op when the caller doesn't care about warnings
  const warn = args.onWarn ?? (() => {});
  const discoveredAgents = await listM8tAgents({ credential: args.credential, projectEndpoint: args.project.endpoint });
  const discovered: Record<string, string> = {};
  for (const a of discoveredAgents) {
    const persona = a.metadata.persona;
    if (persona) discovered[persona] = a.name;
  }

  const loaderPath = path.join(args.contentDir, "targets/foundry/brain-loader.md");

  return {
    async applyPersona(a: ComponentAction, ctx: ApplyCtx) {
      const personaName = a.personaName;
      if (!personaName) {
        throw new LocalCliError({ code: "PLATFORM_PERSONA_NO_NAME", message: "persona action missing personaName" });
      }
      const agentName = discovered[personaName];
      if (!agentName) {
        warn(`persona '${personaName}' is not deployed on this install — skipping.`);
        return "absent";
      }
      const personaItem = ctx.manifest.components.personas.items[personaName];
      return applyPersona(a, ctx, {
        agentName,
        project: args.project,
        personaDir: path.join(ctx.contentDir, personaItem.path),
        loaderPath,
      });
    },

    async applyAgent(a: ComponentAction, ctx: ApplyCtx) {
      if (a.component !== "codingAgent" && a.component !== "azureExecutor") {
        throw new LocalCliError({
          code: "PLATFORM_AGENT_BAD_COMPONENT",
          message: `applyAgent called with non-hosted component '${a.component}'`,
        });
      }
      const dir = HOSTED_AGENT_PERSONA_DIR[a.component];
      const agentName = dir ? discovered[dir] : undefined;
      if (!agentName) {
        warn(`hosted agent for component '${a.component}' is not deployed on this install — skipping.`);
        return "absent";
      }
      const componentRef = ctx.manifest.components[a.component];
      return applyAgentImage(a, ctx, {
        agentName,
        project: args.project,
        ghcrImage: `${componentRef.ref}:${a.to}`,
      });
    },

    async applyGateway(a: ComponentAction, ctx: ApplyCtx) {
      return applyGatewayImage(a, ctx, args.gatewayResourceId);
    },

    async applyInfra(a: ComponentAction, ctx: ApplyCtx) {
      const target = infraTargetSha(ctx.manifest, args.tree);
      // The updater job runs the infra bicep from the STAMPED
      // system/infra-params (the real, full BicepParams captured by
      // enable-auto-update), NOT a from-scratch reconstruction — which cannot
      // recover clientId/tenantId/foundry endpoint and dies with an empty
      // endpoint ("Cannot parse the Foundry account name…", the live-smoke
      // finding). imageRef stays the TARGET manifest's gateway (that's what this
      // converge is rolling to); every other field comes from the stamped row.
      const infraTable = await openInfraParamsTable({ credential: args.credential, subscriptionId: args.subscriptionId, resourceGroup: args.resourceGroup });
      const stamped = (await readInfraParams(infraTable))?.bicep;
      // The updater MI is RG-scoped (never subscription-scope — the A2 trust
      // boundary), so main.bicep's subscription-scoped module MUST be skipped on
      // an auto-update converge. Loud + honest: sub-scoped infra changes never
      // flow through the auto-update rail (they persist idempotently from the
      // founder deploy; a release that changes them ships as a founder-CLI run).
      args.onProgress?.("subscription-scoped modules skipped: updater is RG-scoped; sub-level changes require a founder-run CLI converge.");
      // Read the live voice-relay shared secret back so this infra converge does not
      // re-mint it (main.bicep's newGuid() default) and needlessly roll the relay.
      // The gateway name is derived from the same verified suffix the deploy uses.
      const gatewayName = args.suffix ? `m8t-gateway-${args.suffix}` : undefined;
      const voiceInternalSecret = gatewayName
        ? await readLiveVoiceInternalSecret({
            gatewayName,
            resourceGroup: args.resourceGroup,
            subscriptionId: args.subscriptionId,
          })
        : "";
      // A gateway name was available, so an empty result here means the read failed or
      // the env var is missing — not that there was nothing to read (readLiveVoiceInternalSecret
      // swallows every error to "", by design). Surfacing this matters because it's exactly
      // the condition that lets main.bicep's newGuid() default re-mint the secret.
      if (gatewayName && !voiceInternalSecret) {
        args.onProgress?.(
          `could not read the live voice-relay secret (M8T_INTERNAL_SECRET) off gateway '${gatewayName}' — the read failed or the variable is missing. This infra converge will re-mint the secret, rolling BOTH the gateway and the voice relay.`,
        );
      }
      const opts = {
        suffix: args.suffix,
        assignSubscriptionRoles: false,
        ...(stamped
          ? {
              tenantId: stamped.tenantId,
              clientId: stamped.clientId,
              foundryEndpoint: stamped.foundryProjectEndpoint,
              foundryResourceId: stamped.foundryResourceId,
              acrPullIdentity: stamped.acrPullIdentityResourceId,
              acrResourceId: stamped.acrResourceId,
              ...(stamped.foundryTracingMode ? { foundryTracingMode: stamped.foundryTracingMode as FoundryTracingMode } : {}),
              ...(stamped.channelUrl ? { channelUrl: stamped.channelUrl } : {}),
              ...(stamped.location ? { location: stamped.location } : {}),
            }
          : {}),
        ...(voiceInternalSecret ? { voiceInternalSecret } : {}),
      };
      return applyInfraDeploy(a, ctx, target, opts);
    },

    async healthGate(_ctx: ApplyCtx, applied: ComponentAction[]) {
      for (const a of applied) {
        let agentName: string | undefined;
        if (a.component === "personas" && a.personaName) {
          agentName = discovered[a.personaName];
        } else if (a.component === "codingAgent" || a.component === "azureExecutor") {
          const dir = HOSTED_AGENT_PERSONA_DIR[a.component];
          agentName = dir ? discovered[dir] : undefined;
        }
        if (!agentName) continue;
        // A real gate: awaitAgentQueryable THROWS (AGENT_NOT_QUERYABLE) on a genuine
        // not-queryable timeout, so applyPlan's catch stamps the converge "failed".
        await awaitAgentQueryable({ credential: args.credential, projectEndpoint: args.project.endpoint, agentName, onProgress: args.onProgress });
      }

      if (args.gatewayUrl) {
        const read = await readGatewayVersion(args.gatewayUrl);
        switch (read.kind) {
          case "version":
            args.onProgress?.(`gateway reachable (version: ${read.version}).`);
            break;
          case "no-version":
            args.onProgress?.("gateway reachable (version: unknown).");
            break;
          case "auth-gated":
            args.onProgress?.(`gateway reachable (auth-gated, HTTP ${read.status.toString()}).`);
            break;
          case "http-error":
            warn(`gateway health probe returned HTTP ${read.status.toString()} — not treated as fatal.`);
            break;
          case "unreachable":
            warn(`gateway health probe failed: ${read.detail} — not treated as fatal.`);
            break;
        }
      }
    },

    async readStamp() {
      return readStamp({ credential: args.credential, subscriptionId: args.subscriptionId, resourceGroup: args.resourceGroup });
    },

    async writeStamp(s: InstalledStamp) {
      await writeStamp({
        credential: args.credential,
        subscriptionId: args.subscriptionId,
        resourceGroup: args.resourceGroup,
        stamp: { ...s, cli: CLI_VERSION },
        onProgress: args.onProgress,
      });
    },

    async writeInfraParams(ctx: ApplyCtx, _suffix: string) {
      // Re-stamp system/infra-params from the EXISTING row (the full params captured by
      // enable-auto-update), refreshing only imageRef to the target gateway.
      // The updater's ApplyCtx carries no Foundry endpoint, so re-resolving from scratch
      // here (resolveBicepParamsForConverge) would die 'Cannot parse the Foundry account
      // name from endpoint:' exactly like applyInfra did — READ, don't reconstruct.
      // The founder CLI path re-writes with real resolvers via its own flow.
      const table = await openInfraParamsTable({ credential: args.credential, subscriptionId: args.subscriptionId, resourceGroup: args.resourceGroup });
      const existing = await readInfraParams(table);
      if (!existing) {
        warn("infra-params re-stamp skipped: no existing row to refresh (enable-auto-update writes the authoritative row).");
        return;
      }
      await writeInfraParamsRow(
        {
          credential: args.credential,
          subscriptionId: args.subscriptionId,
          resourceGroup: args.resourceGroup,
          retryHint: "The role assignment persists — retry 'm8t platform update' in a few minutes.",
          onProgress: args.onProgress,
        },
        {
          ...existing,
          bicep: {
            ...existing.bicep,
            imageRef: gatewayRefFromComponent(ctx.manifest.components.gateway),
          },
          updatedAt: new Date().toISOString(),
        },
      );
    },

    async applySeedRefresh(_a: ComponentAction, ctx: ApplyCtx) {
      let kvUri: string | undefined;
      try {
        kvUri = discoverKvUri(process.env);
      } catch {
        kvUri = undefined;
      }
      if (!kvUri) {
        warn("brainSeeds: no Key Vault URI (set AZURE_KEYVAULT_URI) — skipping seed refresh.");
        // Not converged: a degrade must NOT advance the seed stamp (which would poison the
        // next update into skipping brainSeeds). applyPlan retries on the next converge.
        return { delivered: 0, noop: 0, converged: false };
      }
      const { delivered, noop, failed } = await runSeedRefresh({
        credential: args.credential,
        kvUri,
        manifest: ctx.manifest,
        agents: discoveredAgents,
        version: ctx.manifest.platform.version,
        onProgress: args.onProgress,
        onWarn: args.onWarn,
      });
      if (failed > 0) {
        warn(`brainSeeds: ${failed.toString()} repo(s) failed to receive a seed update — re-run 'm8t platform update --only brainSeeds' to retry.`);
      }
      // Converged only when the pass genuinely reached a coherent state (kvUri present AND no
      // per-repo failures). Any failure → not converged → don't advance the stamp → retried.
      return { delivered, noop, converged: failed === 0 };
    },
  };
}

/** Normalize a version string to the vX.Y.Z form compareSemver expects. */
function toVTag(v: string): string {
  return v.startsWith("v") ? v : `v${v}`;
}

/**
 * Refuse to converge when the running CLI is below the manifest's minimum
 * supported version; warn (but proceed) when it's below the recommended
 * version. `onWarn` defaults to a no-op so callers that only care about the
 * throw path don't need to pass one.
 */
export function assertCliVersionOk(
  manifest: ReleaseManifest,
  runningVersion: string,
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- default no-op when the caller doesn't care about warnings
  onWarn: (m: string) => void = () => {},
): void {
  const cli = manifest.components.cli;
  const running = toVTag(runningVersion);
  const min = toVTag(cli.min);
  const recommended = toVTag(cli.recommended);

  if (compareSemver(running, min) < 0) {
    throw new LocalCliError({
      code: "PLATFORM_CLI_TOO_OLD",
      message: `This CLI (${runningVersion}) is older than the minimum supported version (${cli.min}) for platform ${manifest.platform.tag}.`,
      hint: "Upgrade: 'npm install -g @m8t-stack/cli@latest' (or your package manager's equivalent).",
    });
  }
  if (compareSemver(running, recommended) < 0) {
    onWarn(`This CLI (${runningVersion}) is older than the recommended version (${cli.recommended}) for platform ${manifest.platform.tag}. Consider upgrading.`);
  }
}
